import { useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { errText } from "@/lib/aadhaar";
import { zoyaSession } from "@/lib/zoya";

export const AskTrainer = ({ question, answer, lesson, chapter, flagged, i }) => {
  const { L, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", contact: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const token = localStorage.getItem("rs_student_token");

  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/classroom/doubts", { session_id: zoyaSession(), question, answer, lesson, chapter, lang, ...f },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      setSent(true); setOpen(false);
      toast.success(L("Sent! A RoboSpark trainer will reply to you soon.", "भेज दिया! RoboSpark ट्रेनर जल्दी जवाब देंगे।"));
    } catch (e2) { toast.error(errText(e2.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (sent) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-400/10 text-emerald-700" data-testid={`ask-trainer-sent-${i}`}><CheckCircle2 className="w-3 h-3" />{L("Sent to a trainer", "ट्रेनर को भेजा गया")}</span>;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" data-testid={`ask-trainer-btn-${i}`}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full transition-colors ${flagged ? "bg-[#FFC94D] text-[#0A192F] animate-pulse" : "bg-slate-900/5 text-slate-600 hover:text-[#0A192F] hover:bg-slate-900/5"}`}>
          <LifeBuoy className="w-3 h-3" />{flagged ? L("A trainer can help with this — ask now", "इसमें ट्रेनर मदद करेंगे — अभी पूछें") : L("Ask a trainer", "ट्रेनर से पूछें")}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#E8F0FE] border-slate-200 text-[#0A192F] max-w-md" data-testid="ask-trainer-dialog">
        <DialogHeader>
          <DialogTitle>{L("Ask a RoboSpark trainer", "RoboSpark ट्रेनर से पूछें")}</DialogTitle>
          <DialogDescription>{L("A real trainer will read your doubt and reply personally.", "एक असली ट्रेनर आपका सवाल पढ़कर खुद जवाब देंगे।")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={send} className="space-y-3">
          <p className="text-sm rounded-xl bg-white border border-slate-200 p-3 text-slate-700" data-testid="ask-trainer-question">“{question}”</p>
          {token ? <p className="text-xs text-slate-500">{L("We'll attach your student account so the trainer can reply by email.", "आपका छात्र खाता जुड़ जाएगा ताकि ट्रेनर ईमेल पर जवाब दे सकें।")}</p> : (
            <>
              <Input required minLength={2} maxLength={80} placeholder={L("Your name", "आपका नाम")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-white" data-testid="ask-trainer-name" />
              <Input required maxLength={120} placeholder={L("Email or 10-digit mobile", "ईमेल या 10 अंकों का मोबाइल")} value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} className="bg-white" data-testid="ask-trainer-contact" />
            </>
          )}
          <Button type="submit" disabled={busy} className="w-full rs-cta rounded-full" data-testid="ask-trainer-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : L("Send to trainer", "ट्रेनर को भेजें")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
